import type { ChangeEvent, ChangeEventHandler, MouseEventHandler, ReactNode } from 'react';

import classNames from 'classnames';
import type { FormikContextType } from 'formik';
import { connect as formikConnect } from 'formik';
import noop from 'lodash/noop';
import { compose, mapProps } from 'recompact';

import { MarkdownCopy } from 'src/app/components/Elements/MarkdownCopy';

import { FormError } from '../FormError';

import styles from './styles.module.scss';

interface CheckBoxInputUnwiredProps {
  className: string;
  id: string;
  label: ReactNode;
  name: string;
  disabled: boolean;
  compactStyle: boolean;
  tabIndex: number;
  checked: boolean;
  error: string;
  alternateLabelStyle: boolean;
  hasTopAlignedContent: boolean;
  hasBlueSentenceLabel: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLInputElement>;
  onBlur?: ChangeEventHandler<HTMLInputElement>;
}

export const CheckBoxInputUnwired = ({
  id,
  name,
  label,
  error,
  checked,
  onChange,
  onClick,
  onBlur,
  disabled,
  className,
  compactStyle,
  tabIndex,
  alternateLabelStyle,
  hasTopAlignedContent,
  hasBlueSentenceLabel,
}: CheckBoxInputUnwiredProps) => {
  const errorId = `error-${name}`;
  return (
    <div
      className={classNames(
        className,
        styles.component,
        { [styles.compact]: compactStyle },
        { [styles.checked]: checked },
        { [styles.hasTopAlignedContent]: hasTopAlignedContent },
        { [styles.error]: !!error }
      )}
    >
      <input
        type="checkbox"
        className={styles.input}
        id={id || name}
        name={name}
        onChange={onChange}
        onClick={onClick}
        onBlur={onBlur}
        checked={checked}
        disabled={disabled}
        tabIndex={tabIndex}
        aria-describedby={errorId}
      />
      <div className={styles.checkBox}>
        <div className={styles.checkmark} />
      </div>
      <label
        className={classNames(
          styles.label,
          { [styles.alternateLabelStyle]: alternateLabelStyle },
          { [styles.hasBlueSentenceLabel]: hasBlueSentenceLabel }
        )}
        htmlFor={id || name}
      >
        {typeof label === 'string' ? <MarkdownCopy copy={label} /> : label}
        {error && (
          <>
            <br />
            <FormError errorId={errorId} error={error} className={classNames('error', styles.error)} />
          </>
        )}
      </label>
    </div>
  );
};

CheckBoxInputUnwired.defaultProps = {
  onChange: noop,
  onBlur: noop,
  checked: false,
};

const CheckBoxInputFormikWired = compose(
  formikConnect,
  mapProps(({ formik, ...parentProps }: CheckBoxInputUnwiredProps & { formik: FormikContextType<any> }) => {
    const { name, onChange } = parentProps;
    const { values, touched, errors, handleChange: formikHandleChange, handleBlur } = formik;
    const error = touched[name] && errors[name];
    const checked = !!values[name];
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      formikHandleChange(e);
      onChange?.(e);
    };
    return {
      ...parentProps,
      error,
      checked,
      onChange: handleChange,
      onBlur: handleBlur,
    };
  })
)(CheckBoxInputUnwired);

export default CheckBoxInputFormikWired;
